import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, LayoutGrid, List, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    filterStaff();
  }, [staff, searchTerm]);

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff');
      setStaff(response.data);
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const filterStaff = () => {
    let filtered = staff;
    if (searchTerm) {
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (s.current_club && s.current_club.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (s.department && s.department.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    setFilteredStaff(filtered);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 data-testid="staff-page-title" className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                Coaches
              </h1>
              <p className="text-lg text-zinc-300">{filteredStaff.length} coaches</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-zinc-900/50 border border-white/10 rounded-sm overflow-hidden">
                <button
                  onClick={() => setViewMode('cards')}
                  data-testid="staff-view-mode-cards"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${
                    viewMode === 'cards' ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span className="hidden sm:inline">Cards</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  data-testid="staff-view-mode-list"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${
                    viewMode === 'list' ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>
              <Link
                to="/staff/new"
                data-testid="add-new-staff-button"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-all duration-200"
              >
                <Plus className="w-5 h-5" strokeWidth={2} />
                Add Coach
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <div className="glass-card p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Search by name, club, or position..."
              data-testid="search-staff-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
            />
          </div>
        </div>

        {filteredStaff.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-zinc-500 text-lg">No coaches found. Add your first coach to get started!</p>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStaff.map((member) => (
              <Link
                key={member.id}
                to={`/staff/${member.id}`}
                data-testid={`staff-item-${member.id}`}
                className="glass-card p-6 card-hover group"
              >
                <div className="flex items-start gap-4 mb-4">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="w-16 h-16 rounded-lg object-cover border border-white/10 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-zinc-800 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-8 h-8 text-zinc-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-white mb-1 truncate">{member.name}</h3>
                    <p className="text-sm text-zinc-400">{member.current_club || 'No club'}</p>
                    {member.department && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-white/10 text-white text-xs font-medium rounded">
                        {member.department}
                      </span>
                    )}
                  </div>
                </div>

                {(member.email || member.phone) && (
                  <div className="pt-4 border-t border-white/5 space-y-1">
                    {member.email && (
                      <p className="text-xs text-zinc-400 truncate">{member.email}</p>
                    )}
                    {member.phone && (
                      <p className="text-xs text-zinc-400">{member.phone}</p>
                    )}
                  </div>
                )}

                {member.salary && (
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Salary</p>
                    <p className="text-lg font-bold tabular-nums text-green-400">
                      £{member.salary.toLocaleString()}/{member.salary_frequency || 'year'}
                    </p>
                  </div>
                )}
              </Link>
            ))}
          </div>
        ) : (
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-black/30">
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Name</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Current Club</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Position</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Email</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Phone</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Salary</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((member) => (
                  <tr
                    key={member.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => window.location.href = `/staff/${member.id}`}
                    data-testid={`staff-row-${member.id}`}
                  >
                    <td className="p-4">
                      <span className="font-bold text-white">{member.name}</span>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-white/10 text-white text-xs font-bold rounded">
                        {member.current_club || '-'}
                      </span>
                    </td>
                    <td className="p-4 text-zinc-400">{member.department || '-'}</td>
                    <td className="p-4 text-zinc-400">{member.email || '-'}</td>
                    <td className="p-4 text-zinc-400">{member.phone || '-'}</td>
                    <td className="p-4 text-right">
                      {member.salary ? (
                        <span className="font-bold text-green-400">
                          £{member.salary.toLocaleString()}/{member.salary_frequency?.charAt(0) || 'y'}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Staff;
